import type { CSSProperties, ReactNode } from 'react';
import { motion } from 'motion/react';
import { cn, seededRandom } from './util';

/**
 * Points of an irregular comic starburst in a 200×200 box centred on (100,100).
 * Deterministic for a given seed so the shape never "jumps" between renders.
 */
export function burstPoints(spikes = 14, seed = 7, inner = 0.62, jitter = 0.12): string {
  const rnd = seededRandom(seed);
  const pts: string[] = [];
  const total = spikes * 2;
  const offset = -Math.PI / 2 + (rnd() - 0.5) * 0.3;
  for (let i = 0; i < total; i++) {
    const outer = i % 2 === 0;
    const base = outer ? 1 - rnd() * jitter : inner + (rnd() - 0.5) * jitter * 0.8;
    const angle = offset + (i / total) * Math.PI * 2 + (rnd() - 0.5) * (Math.PI / total) * 0.7;
    const r = 92 * base;
    pts.push(`${(100 + Math.cos(angle) * r).toFixed(1)},${(100 + Math.sin(angle) * r).toFixed(1)}`);
  }
  return pts.join(' ');
}

export interface BurstProps {
  /** CSS size (number = px). */
  size?: number | string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  spikes?: number;
  seed?: number;
  /** Inner radius ratio (0.5 = spiky, 0.75 = puffy). */
  inner?: number;
  /** Hard ink offset shadow behind the burst. */
  shadow?: boolean;
  /** Second, smaller burst inside (classic two-tone POW). */
  innerFill?: string;
  /** Gentle breathing loop. */
  pulse?: boolean;
  rotate?: number;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** Accessible label; decorative when omitted. */
  label?: string;
}

/** POW! starburst shape with optional centred content (e.g. "VS", a score). */
export function Burst({
  size = 160,
  fill = '#ffde00',
  stroke = '#111',
  strokeWidth = 6,
  spikes = 14,
  seed = 7,
  inner = 0.64,
  shadow = true,
  innerFill,
  pulse,
  rotate = 0,
  children,
  className,
  style,
  label,
}: BurstProps) {
  const pts = burstPoints(spikes, seed, inner);
  const innerPts = innerFill ? burstPoints(spikes, seed + 3, inner + 0.04, 0.08) : '';
  const dim = typeof size === 'number' ? `${size}px` : size;
  return (
    <motion.div
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn('relative inline-grid shrink-0 place-items-center', className)}
      style={{ width: dim, height: dim, rotate, ...style }}
      animate={pulse ? { scale: [1, 1.06, 1], rotate: [rotate, rotate + 2, rotate] } : undefined}
      transition={pulse ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } : undefined}
    >
      <svg viewBox="-8 -8 216 216" className="absolute inset-0 size-full overflow-visible">
        {shadow && (
          <polygon
            points={pts}
            fill={stroke}
            transform="translate(7 7)"
            strokeLinejoin="round"
            stroke={stroke}
            strokeWidth={strokeWidth}
          />
        )}
        <polygon
          points={pts}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
        />
        {innerFill && (
          <polygon
            points={innerPts}
            fill={innerFill}
            transform="translate(100 100) scale(0.7) translate(-100 -100)"
          />
        )}
      </svg>
      {children != null && <div className="relative">{children}</div>}
    </motion.div>
  );
}
